import { Button, Card, Select, Spinner, Toggle } from '@cimpress/react-components';
import { json } from '@codemirror/lang-json';
import CodeMirror from '@uiw/react-codemirror';
import axios from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ApiKeyContext } from '../../App';
import { AnchorImage, Page } from '../../Components';
import { getAuthHeaders } from '../../Auth';
import {
    buildDesignPostBody,
    convertToDesignEndpoint,
    extractDesignDocument,
    type ConversionResult,
} from '../../api/convertToDesignEndpoint';
import { uploadJsonDocumentAndGetDocumentUrl } from '../../api/uploadJsonDocumentAndGetDocumentUrl';
import { getRenderingUrlDocument, getTransientDocumentUrl } from '../../api/urlHelpers';
import config from '../../config';
import { useSearchParamsWithApiKey } from '../../tools';
import {
    AdjustForSurfaceData,
    AutoLayoutData,
    ComposeDesignData,
    ComposeDynamicLineData,
    CompositionGenerateData,
} from './UrlData';

// Helper function to compute JSON diff
interface DiffResult {
    path: string;
    type: 'added' | 'removed' | 'modified' | 'equal';
    leftValue?: any;
    rightValue?: any;
}

function computeJsonDiff(
    left: any,
    right: any,
    path: string = '',
    excludeProps: Set<string> = new Set(),
): DiffResult[] {
    const diffs: DiffResult[] = [];

    // Handle null/undefined
    if (left === null || left === undefined) {
        if (right === null || right === undefined) {
            return [{ path: path || '(root)', type: 'equal', leftValue: left, rightValue: right }];
        }
        return [{ path: path || '(root)', type: 'added', rightValue: right }];
    }
    if (right === null || right === undefined) {
        return [{ path: path || '(root)', type: 'removed', leftValue: left }];
    }

    // Handle primitives
    if (typeof left !== 'object' || typeof right !== 'object') {
        if (left === right) {
            return [{ path: path || '(root)', type: 'equal', leftValue: left, rightValue: right }];
        }
        return [{ path: path || '(root)', type: 'modified', leftValue: left, rightValue: right }];
    }

    // Handle arrays
    if (Array.isArray(left) && Array.isArray(right)) {
        const maxLength = Math.max(left.length, right.length);
        for (let i = 0; i < maxLength; i++) {
            const itemPath = `${path}[${i}]`;
            if (i >= left.length) {
                diffs.push({ path: itemPath, type: 'added', rightValue: right[i] });
            } else if (i >= right.length) {
                diffs.push({ path: itemPath, type: 'removed', leftValue: left[i] });
            } else {
                diffs.push(...computeJsonDiff(left[i], right[i], itemPath, excludeProps));
            }
        }
        return diffs;
    }

    // Handle objects
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    const allKeys = new Set([...leftKeys, ...rightKeys]);

    allKeys.forEach((key) => {
        if (excludeProps.has(key)) {
            return;
        }

        const itemPath = path ? `${path}.${key}` : key;
        if (!(key in left)) {
            diffs.push({ path: itemPath, type: 'added', rightValue: right[key] });
        } else if (!(key in right)) {
            diffs.push({ path: itemPath, type: 'removed', leftValue: left[key] });
        } else {
            diffs.push(...computeJsonDiff(left[key], right[key], itemPath, excludeProps));
        }
    });

    return diffs;
}

type EndpointType = 'autoLayout' | 'composeDesign' | 'composeDynamicLine' | 'composition:generate' | 'adjustForSurface';

const DEFAULT_DATA: Record<EndpointType, string[]> = {
    autoLayout: AutoLayoutData,
    composeDesign: ComposeDesignData,
    composeDynamicLine: ComposeDynamicLineData,
    'composition:generate': CompositionGenerateData,
    adjustForSurface: AdjustForSurfaceData,
};

const endpointOptions = [
    { value: 'composeDesign', label: 'Compose Design' },
    { value: 'composition:generate', label: 'Composition Generate' },
    { value: 'autoLayout', label: 'Auto Layout' },
    { value: 'composeDynamicLine', label: 'Compose Dynamic Line' },
    { value: 'adjustForSurface', label: 'Adjust For Surface (AdaptToProductSurface)' },
];

const LegacyToDesignJobComparison = () => {
    const apiKey = useContext(ApiKeyContext);

    // Search Params
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();

    const endpointTypeFromParams = searchParams.get('endpointType');
    const pageFromParams = searchParams.get('page');

    // Endpoint selection
    const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointType>(
        (endpointTypeFromParams as EndpointType) || 'composeDesign',
    );
    const [currentPage, setCurrentPage] = useState(pageFromParams ? parseInt(pageFromParams, 10) : 0);
    const [enableApiCalls, setEnableApiCalls] = useState(true);
    const [excludedProperties, setExcludedProperties] = useState('id, metadata');
    const [showJsonDiff, setShowJsonDiff] = useState(false);

    // Editable data arrays
    const [editableData, setEditableData] = useState<Record<EndpointType, string[]>>(DEFAULT_DATA);

    // Get current URL data
    const currentUrls = editableData[selectedEndpoint] || [];
    const currentUrl = currentUrls[currentPage] || '';
    const totalPages = currentUrls.length;

    // Conversion state
    const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);

    // API call state
    const [isLoading, setIsLoading] = useState(false);
    const [legacyResponse, setLegacyResponse] = useState<any>(null);
    const [designResponse, setDesignResponse] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [legacyRequestUrl, setLegacyRequestUrl] = useState<string>('');
    // Design jobs are POSTed, so their preview needs a URL for the returned document
    const [designDocumentUrl, setDesignDocumentUrl] = useState<string>('');
    const [designPreviewError, setDesignPreviewError] = useState<string | null>(null);

    // Update search params when state changes
    useEffect(() => {
        const searchParamsObj: Record<string, string> = {};

        if (selectedEndpoint) {
            searchParamsObj['endpointType'] = selectedEndpoint;
        }

        if (currentPage > 0) {
            searchParamsObj['page'] = currentPage.toString();
        }

        setSearchParams(searchParamsObj);
    }, [selectedEndpoint, currentPage, setSearchParams]);

    // Auto-convert when page or endpoint changes
    useEffect(() => {
        if (currentUrl && apiKey) {
            handleConvert();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, selectedEndpoint, currentUrl, apiKey]);

    // Compute JSON diff
    const jsonDiff = useMemo(() => {
        if (!legacyResponse || !designResponse || legacyResponse.error || designResponse.error) {
            return null;
        }

        const legacyData = legacyResponse.data || legacyResponse;
        const designData = designResponse.data || designResponse;

        const excludeSet = new Set(
            excludedProperties
                .split(',')
                .map((prop) => prop.trim())
                .filter((prop) => prop.length > 0),
        );

        return computeJsonDiff(legacyData, designData, '', excludeSet);
    }, [legacyResponse, designResponse, excludedProperties]);

    const isLocalBackend = config.backendServiceUrl.includes('localhost');

    // Generate preview props
    const legacyPreview = useMemo(() => {
        if (!legacyRequestUrl) return null;
        if (isLocalBackend && legacyResponse && !legacyResponse.error) {
            const doc = legacyResponse.data || legacyResponse;
            if (!doc) return null;
            try {
                const transientUrl = getTransientDocumentUrl(doc);
                const previewUrl = getRenderingUrlDocument(transientUrl, 0);
                return { documentUrl: legacyRequestUrl, previewUrl, alt: 'Legacy Endpoint Result' };
            } catch {
                return null;
            }
        }
        const previewUrl = getRenderingUrlDocument(legacyRequestUrl, 0);
        if (!previewUrl) return null;
        return { documentUrl: legacyRequestUrl, previewUrl, alt: 'Legacy Endpoint Result' };
    }, [legacyRequestUrl, legacyResponse, isLocalBackend]);

    useEffect(() => {
        setDesignDocumentUrl('');
        setDesignPreviewError(null);
        const doc = designResponse && !designResponse.error ? designResponse.data : null;
        if (!doc) return;
        try {
            setDesignDocumentUrl(getTransientDocumentUrl(doc));
            return;
        } catch {
            // Too large for a transient URL; fall back to temporary storage
        }
        const controller = new AbortController();
        uploadJsonDocumentAndGetDocumentUrl(structuredClone(doc), controller.signal, 1)
            .then((url) => !controller.signal.aborted && setDesignDocumentUrl(url))
            .catch((err) => {
                if (!controller.signal.aborted) {
                    setDesignPreviewError(`Preview upload failed: ${err?.message ?? err}`);
                }
            });
        return () => controller.abort();
    }, [designResponse]);

    const designPreview = useMemo(() => {
        if (!designDocumentUrl) return null;
        const previewUrl = getRenderingUrlDocument(designDocumentUrl, 0);
        if (!previewUrl) return null;
        return { documentUrl: designDocumentUrl, previewUrl, alt: 'Design Endpoint Result' };
    }, [designDocumentUrl]);

    const handleConvert = async () => {
        if (!apiKey) {
            setError('API key is required. Please set it in the header.');
            return;
        }

        const result = convertToDesignEndpoint(currentUrl, config.backendServiceUrl, apiKey);
        setConversionResult(result);
        setError(result.success ? null : result.error || null);

        // Clear previous API results
        setLegacyResponse(null);
        setDesignResponse(null);
        setLegacyRequestUrl('');

        if (result.success && enableApiCalls) {
            await makeApiCalls(result);
        }
    };

    const makeApiCalls = async (result: ConversionResult) => {
        if (!result.success || !apiKey) return;

        setIsLoading(true);
        setError(null);

        try {
            // Legacy endpoints are GET (URL reused for preview); design jobs are POST with a JWT
            setLegacyRequestUrl(result.originalUrl);

            const [legacyResult, designResult] = await Promise.all([
                axios.get(result.originalUrl).catch((err) => ({
                    error: true,
                    message: err.response?.data?.message || err.message || 'Legacy API call failed',
                    status: err.response?.status,
                })),
                getAuthHeaders()
                    .then((headers) =>
                        axios.post(result.designEndpointUrl, buildDesignPostBody(result.designEndpointBody), {
                            headers,
                        }),
                    )
                    // POST wraps the document in { result: { cimpressDocuments }, designReport }
                    .then((res) => {
                        const doc = extractDesignDocument(res.data);
                        if (!doc) {
                            return {
                                error: true,
                                message: 'Design response contained no document',
                                status: res.status,
                            };
                        }
                        return { data: doc, designReport: res.data?.designReport };
                    })
                    .catch((err) => ({
                        error: true,
                        message: err.response?.data?.message || err.message || 'Design API call failed',
                        status: err.response?.status,
                    })),
            ]);

            setLegacyResponse(legacyResult);
            setDesignResponse(designResult);
        } catch (err: any) {
            setError(err.message || 'Unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetryApiCalls = async () => {
        if (conversionResult?.success) {
            await makeApiCalls(conversionResult);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleEndpointChange = (selected: { value: string; label: string } | null) => {
        if (selected) {
            console.log('Endpoint changed to:', selected.value);
            setSelectedEndpoint(selected.value as EndpointType);
            setCurrentPage(0); // Reset to first page when changing endpoint
        }
    };

    const handleDataArrayChange = (value: string) => {
        try {
            // Parse the JSON array
            const parsedArray = JSON.parse(value);
            if (Array.isArray(parsedArray) && parsedArray.every((item) => typeof item === 'string')) {
                setEditableData((prev) => ({
                    ...prev,
                    [selectedEndpoint]: parsedArray,
                }));
                // Reset to first page if current page is out of bounds
                if (currentPage >= parsedArray.length) {
                    setCurrentPage(0);
                }
            }
        } catch (err) {
            // Invalid JSON, don't update
            console.error('Invalid JSON array:', err);
        }
    };

    const handleResetData = () => {
        setEditableData(DEFAULT_DATA);
        setCurrentPage(0);
    };

    // Get the selected option object for the Select component
    const selectedOption = endpointOptions.find((opt) => opt.value === selectedEndpoint);

    return (
        <Page>
            <Page.Title>Legacy to Design Job Comparison</Page.Title>
            <Page.Content fullWidth>
                <Card>
                    <h2 style={{ marginTop: 0 }}>Compare Legacy Endpoints with /api/v3/design</h2>
                    <p style={{ marginBottom: '24px', color: '#666' }}>
                        Select an endpoint type and navigate through sample URLs to compare legacy endpoints with the
                        new /api/v3/design/{'{designJob}'} endpoint. Legacy endpoints are called with GET; design jobs
                        are called with POST using your login token.
                    </p>

                    {/* Endpoint Selection and Pagination */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                                    Endpoint Type:
                                </label>
                                <Select
                                    value={selectedOption}
                                    onChange={handleEndpointChange}
                                    options={endpointOptions}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Toggle
                                    on={enableApiCalls}
                                    onClick={() => setEnableApiCalls(!enableApiCalls)}
                                    size="sm"
                                />
                                <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>Call APIs</span>
                            </div>
                        </div>

                        {/* Editable Data Array Section */}
                        <div style={{ marginBottom: '16px' }}>
                            <details>
                                <summary
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        color: '#0088A9',
                                        fontWeight: 'bold',
                                        marginBottom: '8px',
                                        padding: '8px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                    }}
                                >
                                    📝 Edit URL Data Array ({currentUrls.length} URLs) - Click to expand
                                </summary>
                                <Card style={{ padding: '16px', marginTop: '8px', backgroundColor: '#f9f9f9' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                            URL Data Array for {selectedOption?.label}:
                                        </label>
                                        <Button onClick={handleResetData} variant="secondary" size="sm">
                                            Reset to Default
                                        </Button>
                                    </div>
                                    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                        <CodeMirror
                                            value={JSON.stringify(currentUrls, null, 2)}
                                            height="200px"
                                            extensions={[json()]}
                                            onChange={handleDataArrayChange}
                                            basicSetup={{
                                                lineNumbers: true,
                                                highlightActiveLineGutter: false,
                                                highlightActiveLine: false,
                                                foldGutter: true,
                                            }}
                                            style={{ wordWrap: 'break-word' }}
                                        />
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                        Edit the JSON array to add, remove, or modify URLs. The array must contain valid
                                        strings.
                                    </div>
                                </Card>
                            </details>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                gap: '16px',
                                alignItems: 'center',
                                padding: '12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                            }}
                        >
                            <Button onClick={handlePreviousPage} disabled={currentPage === 0} size="sm">
                                Previous
                            </Button>
                            <span
                                style={{ fontSize: '14px', fontWeight: 'bold', minWidth: '150px', textAlign: 'center' }}
                            >
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <Button onClick={handleNextPage} disabled={currentPage >= totalPages - 1} size="sm">
                                Next
                            </Button>
                            {conversionResult?.success && !isLoading && (legacyResponse || designResponse) && (
                                <Button onClick={handleRetryApiCalls} variant="secondary" size="sm">
                                    Retry API Calls
                                </Button>
                            )}
                            {isLoading && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Spinner />
                                    <span style={{ fontSize: '14px', color: '#666' }}>Calling APIs...</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <Card style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#ffebee' }}>
                            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#c62828' }}>Error</h3>
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ef5350',
                                    borderRadius: '4px',
                                    color: '#c62828',
                                }}
                            >
                                {error}
                            </div>
                        </Card>
                    )}

                    {/* Document Previews */}
                    {(legacyPreview || designPreview) && !isLoading && (
                        <div style={{ marginTop: '32px' }}>
                            <h3 style={{ marginBottom: '16px', color: '#0088A9' }}>Response Previews</h3>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px',
                                }}
                            >
                                <Card style={{ padding: '16px' }}>
                                    <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Legacy Endpoint Result</h4>
                                    {legacyPreview ? (
                                        <AnchorImage {...legacyPreview} useLoadingTransition />
                                    ) : (
                                        <span style={{ color: '#999' }}>No preview available</span>
                                    )}
                                </Card>
                                <Card style={{ padding: '16px' }}>
                                    <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Design Endpoint Result</h4>
                                    {designPreview ? (
                                        <AnchorImage {...designPreview} useLoadingTransition />
                                    ) : (
                                        <span style={{ color: designPreviewError ? '#c00' : '#999' }}>
                                            {designPreviewError ?? 'No preview available'}
                                        </span>
                                    )}
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* Conversion Result - Request Comparison */}
                    {conversionResult?.success && (
                        <div style={{ marginTop: '32px' }}>
                            <h3 style={{ marginBottom: '16px', color: '#0088A9' }}>Request Comparison</h3>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px',
                                    marginBottom: '32px',
                                }}
                            >
                                {/* Legacy Endpoint Request */}
                                <Card style={{ padding: '16px', minWidth: 0 }}>
                                    <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#333' }}>
                                        Legacy Endpoint ({conversionResult.endpointType})
                                    </h4>
                                    <div
                                        style={{
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        <strong>GET</strong> {new URL(conversionResult.originalUrl).origin}
                                        {new URL(conversionResult.originalUrl).pathname}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <details>
                                            <summary
                                                style={{
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: '#0088A9',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                Full URL with parameters (click to expand)
                                            </summary>
                                            <div
                                                style={{
                                                    padding: '8px',
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-all',
                                                    maxHeight: '200px',
                                                    overflow: 'auto',
                                                }}
                                            >
                                                {conversionResult.originalUrl}
                                            </div>
                                        </details>
                                    </div>
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ fontSize: '12px' }}>Query Parameters:</strong>
                                        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                            <CodeMirror
                                                value={JSON.stringify(conversionResult.originalParams, null, 2)}
                                                height="300px"
                                                extensions={[json()]}
                                                editable={false}
                                                basicSetup={{
                                                    lineNumbers: true,
                                                    highlightActiveLineGutter: false,
                                                    highlightActiveLine: false,
                                                    foldGutter: false,
                                                }}
                                                style={{ wordWrap: 'break-word' }}
                                            />
                                        </div>
                                    </div>
                                </Card>

                                {/* Design Endpoint Request */}
                                <Card style={{ padding: '16px', minWidth: 0 }}>
                                    <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#333' }}>
                                        Design Endpoint (/api/v3/design/{'{designJob}'})
                                    </h4>
                                    <div
                                        style={{
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        <strong>POST</strong> {conversionResult.designEndpointUrl}
                                    </div>
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ fontSize: '12px' }}>Request Body:</strong>
                                        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                            <CodeMirror
                                                value={JSON.stringify(
                                                    buildDesignPostBody(conversionResult.designEndpointBody),
                                                    null,
                                                    2,
                                                )}
                                                height="300px"
                                                extensions={[json()]}
                                                editable={false}
                                                basicSetup={{
                                                    lineNumbers: true,
                                                    highlightActiveLineGutter: false,
                                                    highlightActiveLine: false,
                                                    foldGutter: false,
                                                }}
                                                style={{ wordWrap: 'break-word' }}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* JSON Diff Section */}
                    {
                        <div style={{ marginTop: '32px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <h3 style={{ margin: 0, color: '#0088A9' }}>Response Comparison</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Toggle
                                        on={showJsonDiff}
                                        onClick={() => setShowJsonDiff(!showJsonDiff)}
                                        size="sm"
                                    />
                                    <span style={{ fontSize: '14px', color: '#666' }}>Show JSON Diff</span>
                                </div>
                            </div>
                            {showJsonDiff && (
                                <>
                                    {/* Raw Response JSON Display */}
                                    {conversionResult?.success && (legacyResponse || designResponse) && !isLoading && (
                                        <div style={{ marginTop: '32px' }}>
                                            <h3 style={{ marginBottom: '16px', color: '#0088A9' }}>
                                                Raw Response Data
                                            </h3>
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: '16px',
                                                }}
                                            >
                                                {legacyResponse && (
                                                    <Card style={{ padding: '16px', minWidth: 0 }}>
                                                        <h4
                                                            style={{
                                                                marginTop: 0,
                                                                marginBottom: '12px',
                                                                color: '#333',
                                                            }}
                                                        >
                                                            Legacy Endpoint Response
                                                        </h4>
                                                        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                                            <CodeMirror
                                                                value={JSON.stringify(
                                                                    legacyResponse.error
                                                                        ? legacyResponse
                                                                        : legacyResponse.data || legacyResponse,
                                                                    null,
                                                                    2,
                                                                )}
                                                                height="300px"
                                                                extensions={[json()]}
                                                                editable={false}
                                                                basicSetup={{
                                                                    lineNumbers: true,
                                                                    highlightActiveLineGutter: false,
                                                                    highlightActiveLine: false,
                                                                    foldGutter: false,
                                                                }}
                                                                style={{ wordWrap: 'break-word' }}
                                                            />
                                                        </div>
                                                    </Card>
                                                )}

                                                {designResponse && (
                                                    <Card style={{ padding: '16px', minWidth: 0 }}>
                                                        <h4
                                                            style={{
                                                                marginTop: 0,
                                                                marginBottom: '12px',
                                                                color: '#333',
                                                            }}
                                                        >
                                                            Design Endpoint Response
                                                        </h4>
                                                        {designResponse.designReport && (
                                                            <div
                                                                style={{
                                                                    marginBottom: '8px',
                                                                    fontSize: '12px',
                                                                    wordBreak: 'break-all',
                                                                }}
                                                            >
                                                                <strong>Design report:</strong>{' '}
                                                                {JSON.stringify(designResponse.designReport)}
                                                            </div>
                                                        )}
                                                        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                                                            <CodeMirror
                                                                value={JSON.stringify(
                                                                    designResponse.error
                                                                        ? designResponse
                                                                        : designResponse.data || designResponse,
                                                                    null,
                                                                    2,
                                                                )}
                                                                height="300px"
                                                                extensions={[json()]}
                                                                editable={false}
                                                                basicSetup={{
                                                                    lineNumbers: true,
                                                                    highlightActiveLineGutter: false,
                                                                    highlightActiveLine: false,
                                                                    foldGutter: false,
                                                                }}
                                                                style={{ wordWrap: 'break-word' }}
                                                            />
                                                        </div>
                                                    </Card>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <Card style={{ padding: '16px' }}>
                                        <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                                            Comparing Legacy (left) vs Design (right) endpoint responses
                                            {excludedProperties && (
                                                <span>
                                                    {' '}
                                                    (excluding:{' '}
                                                    <strong>
                                                        {excludedProperties
                                                            .split(',')
                                                            .map((p) => p.trim())
                                                            .filter((p) => p)
                                                            .join(', ')}
                                                    </strong>
                                                    )
                                                </span>
                                            )}
                                            :
                                        </div>
                                        <div
                                            style={{
                                                maxHeight: '500px',
                                                overflow: 'auto',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <table
                                                style={{
                                                    width: '100%',
                                                    fontSize: '12px',
                                                    fontFamily: 'monospace',
                                                    borderCollapse: 'collapse',
                                                }}
                                            >
                                                <thead
                                                    style={{
                                                        position: 'sticky',
                                                        top: 0,
                                                        backgroundColor: '#f5f5f5',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <tr>
                                                        <th
                                                            style={{
                                                                padding: '8px',
                                                                textAlign: 'left',
                                                                borderBottom: '2px solid #ddd',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            Path
                                                        </th>
                                                        <th
                                                            style={{
                                                                padding: '8px',
                                                                textAlign: 'left',
                                                                borderBottom: '2px solid #ddd',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            Status
                                                        </th>
                                                        <th
                                                            style={{
                                                                padding: '8px',
                                                                textAlign: 'left',
                                                                borderBottom: '2px solid #ddd',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            Legacy Value
                                                        </th>
                                                        <th
                                                            style={{
                                                                padding: '8px',
                                                                textAlign: 'left',
                                                                borderBottom: '2px solid #ddd',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            Design Value
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {jsonDiff
                                                        ?.filter((diff) => diff.type !== 'equal')
                                                        .map((diff, index) => {
                                                            const getBgColor = () => {
                                                                switch (diff.type) {
                                                                    case 'added':
                                                                        return '#e8f5e9';
                                                                    case 'removed':
                                                                        return '#ffebee';
                                                                    case 'modified':
                                                                        return '#fff3e0';
                                                                    default:
                                                                        return 'transparent';
                                                                }
                                                            };

                                                            const getStatusLabel = () => {
                                                                switch (diff.type) {
                                                                    case 'added':
                                                                        return '+ Added';
                                                                    case 'removed':
                                                                        return '- Removed';
                                                                    case 'modified':
                                                                        return '~ Modified';
                                                                    default:
                                                                        return '= Equal';
                                                                }
                                                            };

                                                            const getStatusColor = () => {
                                                                switch (diff.type) {
                                                                    case 'added':
                                                                        return '#2e7d32';
                                                                    case 'removed':
                                                                        return '#c62828';
                                                                    case 'modified':
                                                                        return '#f57c00';
                                                                    default:
                                                                        return '#666';
                                                                }
                                                            };

                                                            return (
                                                                <tr
                                                                    key={index}
                                                                    style={{ backgroundColor: getBgColor() }}
                                                                >
                                                                    <td
                                                                        style={{
                                                                            padding: '8px',
                                                                            borderBottom: '1px solid #eee',
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                    >
                                                                        {diff.path}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            padding: '8px',
                                                                            borderBottom: '1px solid #eee',
                                                                            color: getStatusColor(),
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                    >
                                                                        {getStatusLabel()}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            padding: '8px',
                                                                            borderBottom: '1px solid #eee',
                                                                            maxWidth: '300px',
                                                                            overflow: 'auto',
                                                                            wordBreak: 'break-all',
                                                                            whiteSpace: 'pre-wrap',
                                                                        }}
                                                                    >
                                                                        {diff.leftValue !== undefined
                                                                            ? JSON.stringify(diff.leftValue)
                                                                            : '-'}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            padding: '8px',
                                                                            borderBottom: '1px solid #eee',
                                                                            maxWidth: '300px',
                                                                            overflow: 'auto',
                                                                            wordBreak: 'break-all',
                                                                            whiteSpace: 'pre-wrap',
                                                                        }}
                                                                    >
                                                                        {diff.rightValue !== undefined
                                                                            ? JSON.stringify(diff.rightValue)
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                                            <strong>Legend:</strong>
                                            <span style={{ marginLeft: '16px', color: '#2e7d32' }}>+ Added</span>
                                            <span style={{ marginLeft: '16px', color: '#c62828' }}>- Removed</span>
                                            <span style={{ marginLeft: '16px', color: '#f57c00' }}>~ Modified</span>
                                            {jsonDiff?.filter((d) => d.type !== 'equal').length === 0 && (
                                                <div style={{ marginTop: '8px', color: '#2e7d32', fontWeight: 'bold' }}>
                                                    Responses are identical!
                                                </div>
                                            )}
                                        </div>
                                    </Card>

                                    <div style={{ marginTop: '16px' }}>
                                        <Card style={{ padding: '16px' }}>
                                            <label
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 'bold',
                                                    marginBottom: '4px',
                                                    display: 'block',
                                                }}
                                            >
                                                Exclude Properties from Comparison:
                                            </label>
                                            <input
                                                type="text"
                                                value={excludedProperties}
                                                onChange={(e) => setExcludedProperties(e.target.value)}
                                                placeholder="e.g., id, timestamp, createdAt (comma-separated)"
                                                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                            />
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                Enter property names separated by commas. These properties will be
                                                ignored in the diff comparison.
                                            </div>
                                        </Card>
                                    </div>
                                </>
                            )}
                        </div>
                    }
                </Card>
            </Page.Content>
        </Page>
    );
};

export default LegacyToDesignJobComparison;
